import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import ModalAbout from "../components/ModalAbout";
import { makeStyles } from "@mui/styles";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import Options from "../components/Options";
import { changeTab } from "../redux/TabSlice";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import {
  getGuidance,
  getLaws,
  getSelectedGuidance,
  getSelectedLaw,
  setSelectedGuide,
} from "../redux/OffenceSlice";
import { getTitleFromAppContent } from "../helpers/utils";
import { trackPageView } from "../helpers/api";
import ArrowBack from "../assets/img/HumQadamHome/arrowBack.svg";
import { getAllServiceProviders } from "../redux/OrganizationSlice";

const useStyles = makeStyles((theme) => ({
  bgcolor: {
    background: "#FFFFFF",
  },

  sectionbg: {
    background: theme.palette.primary.main,
  },

  sectionBgLight: {
    backgroundColor: "#F6F1F4",
  },

  lawsBg: {
    backgroundColor: "#BCACE3",
  },

  lawsTextBg: {
    backgroundColor: theme.palette.laws.main,
  },

  // Shared intro/description below main heading (Laws, Guidance, Support Providing Orgs)
  introText: {
    fontSize: "0.9375rem",
    color: "#333",
    fontWeight: 400,
    textAlign: "center",
    lineHeight: 1.5,
  },
}));

function EntitiesGrid({
  bgColor,
  bgTextColor,
  img,
  page,
  title,
  nextPage,
  textDark,
}) {
  const classes = useStyles();
  const { selLanguageId } = useSelector((state) => state.language);
  const { selDistrictId } = useSelector((state) => state.district);
  const { offenceLaws } = useSelector((state) => state.offence);
  const { offenceGuidance } = useSelector((state) => state.offence);
  const guidanceLoading = useSelector((state) => state.offence.guidanceLoading);
  const { appContent } = useSelector((state) => state.appContent);
  const [description, setDescription] = useState("");
  const [openAbout, setOpenAbout] = useState(false);
  const { providers } = useSelector((state) => state.organizations);
  const [tabSelect, setTabSelect] = useState(page);
  const [showData, setShowData] = useState([]);
  const dispatch = useDispatch();
  const { selProvinceId } = useSelector((state) => state.province);
  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => {
    setOpenAbout(false);
  };

  useEffect(() => {
    trackPageView(page === "Laws" ? "Laws Entities" : "Guidance Entities", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [page, selProvinceId, selDistrictId, selLanguageId]);

  useEffect(() => {
    dispatch(setSelectedGuide(null));
  }, [dispatch]);

  useEffect(() => {
    if (selLanguageId) {
      if (page === "Laws") {
        dispatch(getLaws({ langId: selLanguageId }));
      }
    }
  }, [selLanguageId, page, dispatch]);

  useEffect(() => {
    if (!selLanguageId) return;
    if (page !== "StepGuide") return;
    dispatch(
      getGuidance({
        district: selDistrictId,
        langId: selLanguageId,
        provinceId: selProvinceId,
      })
    );
    if (providers.length === 0 && selDistrictId) {
      dispatch(
        getAllServiceProviders({ selDistrictId, langId: selLanguageId })
      );
    }
  }, [
    selLanguageId,
    selDistrictId,
    selProvinceId,
    page,
    dispatch,
    providers.length,
  ]);

  useEffect(() => {
    dispatch(changeTab(tabSelect));
  }, [tabSelect, dispatch]);

  useEffect(() => {
    if (page === "Laws") {
      setShowData(offenceLaws);
    } else {
      setShowData(offenceGuidance);
    }
  }, [page, offenceLaws, offenceGuidance]);

  const handleClick = (nextPage, item) => {
    const itemId = item._id || item.id; // MongoDB uses _id
    if (page === "Laws") {
      dispatch(getSelectedLaw({ id: itemId, langId: selLanguageId }));
    } else {
      const hasSteps =
        item.guidance_steps &&
        Array.isArray(item.guidance_steps) &&
        item.guidance_steps.length > 0;
      dispatch(setSelectedGuide(item));
      if (!hasSteps && selDistrictId) {
        dispatch(
          getSelectedGuidance({
            id: itemId,
            langId: selLanguageId,
            district: selDistrictId,
          })
        );
      } else if (!hasSteps && !selDistrictId) {
        dispatch(
          getSelectedGuidance({
            id: itemId,
            langId: selLanguageId,
          })
        );
      }
    }
    setTabSelect(nextPage);
  };

  useEffect(() => {
    if (appContent) {
      if (page === "Laws") {
        setDescription(getTitleFromAppContent(appContent, "EntitiesLawDes"));
      } else {
        setDescription(getTitleFromAppContent(appContent, "EntitiesGuideDes"));
      }
    }
  }, [appContent, page]);

  return (
    <div
      className={` w-full pb-10 flex flex-col justify-between`}
      style={{ background: bgColor }}
    >
      <Header />
      <Options handleOpen={handleOpen} />
      {!selLanguageId ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Select a language to load this section.
          </Typography>
        </Box>
      ) : page === "StepGuide" && guidanceLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : page === "Laws" && showData?.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : showData?.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", px: 2, py: 4 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {page === "StepGuide"
              ? "No step-by-step guidance is available for your area right now. Try another district in Settings, or check again later."
              : "No items to show."}
          </Typography>
        </Box>
      ) : (
        <div className="flex flex-col justify-center content-center items-center mb-20">
          <img className=" w-12 mb-2" src={img} alt="Emergency" />
          <Typography
            style={{ color: textDark ? textDark : bgTextColor }}
            className="text-lg font-bold my-2"
            variant="p"
          >
            {title}
          </Typography>
          <Typography className={clsx("text-center w-4/6 my-2", classes.introText)} variant="p">
            {description}
          </Typography>

          <Grid className="p-5" container spacing={2}>
            {showData.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                lg={2}
                key={index}
                className="flex justify-center"
              >
                <div
                  onClick={() => {
                    handleClick(nextPage, item);
                  }}
                  className={clsx(
                    "cursor-pointer py-5 w-full sm:w-36 rounded-3xl"
                  )}
                  style={{ background: bgTextColor }}
                >
                  <img
                    className="h-16 mb-5 mx-auto"
                    src={item.icon}
                    alt="Guard"
                  />
                  <Typography
                    color={"white"}
                    className="block text-center text-xs"
                    variant="p"
                  >
                    {item.title}
                  </Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      )}
      <img
        alt="Arrow Back"
        onClick={() => setTabSelect("HomePage")}
        src={ArrowBack}
        className="cursor-pointer self-start ml-5"
      />
      <div>
        <ModalAbout open={openAbout} handleClose={handleClose} />
      </div>
    </div>
  );
}

export default EntitiesGrid;
